import { useEffect, useRef, useState } from 'react';
import {
  useViewerStore, ViewportMode, getViewportMode, labelForMode, shortLabelForMode,
} from '../stores/viewerStore';
import styles from './ModePicker.module.css';

const MPR_MODES: ViewportMode[] = ['mpr-ax', 'mpr-sag', 'mpr-cor'];
const MIP_MODES: ViewportMode[] = ['mip-ax', 'mip-sag', 'mip-cor'];
const SLAB_PRESETS = [5, 10, 20, 50, 100];

const VR_PRESETS = [
  'CT-Bone',
  'CT-Soft-Tissue',
  'CT-Lung',
  'CT-Fat',
  'CT-Coronary-Arteries',
  'CT-Chest-Contrast-Enhanced',
  'CT-Chest-Vessels',
  'CT-AAA',
  'CT-MIP',
  'MR-Default',
  'MR-MIP',
];

/** Modalities where MPR / MIP / 3D-VR make clinical sense. Excludes 2D and
 *  non-image modalities (MG, CR, DX, US, XA, RF, ES, GM, OP, SR, DOC, PDF, ...).
 *  Add here if a customer complains a legitimately-volumetric modality is
 *  missing (e.g. some 3D US acquisitions may qualify). */
const VOLUMETRIC_MODALITIES = new Set([
  'CT', 'MR', 'PT', 'NM', 'MG3D',   // MG3D is DBT / tomo — 3D breast
]);

export function isVolumetricModality(modality: string | undefined | null): boolean {
  if (!modality) return true;   // unknown -> permissive (don't hide by mistake)
  return VOLUMETRIC_MODALITIES.has(modality.toUpperCase());
}

interface Props {
  viewportIndex: number;
  /** Modality of the series currently loaded in this viewport (or the
   *  study's modality if no series has been assigned yet). Determines
   *  whether MPR / MIP / 3D-VR sections are shown — hiding them for
   *  2D-only modalities like MG / CR / US keeps the picker focused on
   *  what's actually applicable to the data. */
  modality?: string;
}

/**
 * Overlay dropdown that lets the user set a single viewport's mode.
 * Sits in the top-left corner of each Cornerstone viewport. Follows the
 * DicomDrive v3 pattern: mode is a per-viewport property, layout is
 * independent grid geometry.
 */
export default function ModePicker({ viewportIndex, modality }: Props) {
  const viewportModes = useViewerStore((s) => s.viewportModes);
  const viewportSlabs = useViewerStore((s) => s.viewportSlabs);
  const viewport3DPresets = useViewerStore((s) => s.viewport3DPresets);
  const setViewportMode = useViewerStore((s) => s.setViewportMode);
  const setViewportSlab = useViewerStore((s) => s.setViewportSlab);
  const setViewport3DPreset = useViewerStore((s) => s.setViewport3DPreset);

  const mode = getViewportMode(viewportModes, viewportIndex);
  const slab = viewportSlabs[viewportIndex] ?? 50;
  const preset = viewport3DPresets[viewportIndex] ?? 'CT-Bone';
  const showVolumeModes = isVolumetricModality(modality);

  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  function pick(m: ViewportMode) {
    setViewportMode(viewportIndex, m);
    setOpen(false);
  }

  const isMip = MIP_MODES.includes(mode);

  return (
    <div
      className={styles.wrap}
      ref={wrapRef}
      onMouseDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className={`${styles.pill} ${mode !== 'stack' ? styles.pillActive : ''}`}
        onClick={() => setOpen((v) => !v)}
        title="Change viewport mode"
      >
        {shortLabelForMode(mode)}
        <span className={styles.caret}>▾</span>
      </button>

      {open && (
        <div className={styles.menu}>
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Stack</div>
            <button
              className={`${styles.item} ${mode === 'stack' ? styles.itemActive : ''}`}
              onClick={() => pick('stack')}
            >
              {labelForMode('stack')}
            </button>
          </div>

          {showVolumeModes && (
            <>
              <div className={styles.section}>
                <div className={styles.sectionLabel}>MPR</div>
                {MPR_MODES.map((m) => (
                  <button
                    key={m}
                    className={`${styles.item} ${mode === m ? styles.itemActive : ''}`}
                    onClick={() => pick(m)}
                  >
                    {labelForMode(m)}
                  </button>
                ))}
              </div>

              <div className={styles.section}>
                <div className={styles.sectionLabel}>MIP</div>
                {MIP_MODES.map((m) => (
                  <button
                    key={m}
                    className={`${styles.item} ${mode === m ? styles.itemActive : ''}`}
                    onClick={() => pick(m)}
                  >
                    {labelForMode(m)}
                  </button>
                ))}
                {isMip && (
                  <div className={styles.slabRow}>
                    <span style={{ color: '#8892a4', fontSize: 10 }}>Slab</span>
                    {SLAB_PRESETS.map((mm) => (
                      <button
                        key={mm}
                        className={`${styles.slabBtn} ${slab === mm ? styles.slabBtnActive : ''}`}
                        onClick={() => setViewportSlab(viewportIndex, mm)}
                        title={`${mm} mm slab`}
                      >
                        {mm}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.section}>
                <div className={styles.sectionLabel}>3D</div>
                <button
                  className={`${styles.item} ${mode === '3d-vr' ? styles.itemActive : ''}`}
                  onClick={() => pick('3d-vr')}
                >
                  {labelForMode('3d-vr')}
                </button>
                {mode === '3d-vr' && (
                  <select
                    className={styles.presetSelect}
                    value={preset}
                    onChange={(e) => setViewport3DPreset(viewportIndex, e.target.value)}
                  >
                    {VR_PRESETS.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                )}
              </div>
            </>
          )}
          {!showVolumeModes && (
            <div className={styles.section}>
              <div className={styles.sectionLabel} style={{ opacity: 0.6, fontStyle: 'italic' }}>
                MPR / MIP / 3D unavailable for this modality
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
